<template>
  <AppClientSchoolLayout title="アプリ進捗">
    <div class="max-w-7xl mx-auto px-6 py-7">
      <div class="mb-5">
        <h1 class="text-2xl font-bold text-gray-900">
          アプリ進捗
        </h1>
        <p class="mt-2 text-sm text-gray-500">
          学生ごとのアプリ学習の進捗状況を確認できます。
        </p>
      </div>

      <div class="bg-white border border-gray-200 shadow-sm rounded-lg mb-6">
        <div class="px-5 py-4 border-b border-gray-200">
          <h2 class="text-base font-semibold text-gray-900">
            検索条件
          </h2>
        </div>
        <div class="p-5">
          <div class="grid grid-cols-5 gap-8 items-end">
            <label class="text-xs font-semibold text-gray-600">
              ユーザID
              <input
                v-model="form.id"
                type="search"
                class="mt-2 form-input border border-gray-300 outline-none rounded-md py-2.5 px-4 bg-white placeholder-gray-400 text-gray-700 appearance-none w-full block focus:outline-none"
                placeholder="ユーザIDを入力" />
            </label>
            <label class="text-xs font-semibold text-gray-600">
              学籍番号
              <input
                v-model="form.studentId"
                type="search"
                class="mt-2 form-input border border-gray-300 outline-none rounded-md py-2.5 px-4 bg-white placeholder-gray-400 text-gray-700 appearance-none w-full block focus:outline-none"
                placeholder="学籍番号を入力" />
            </label>
            <label class="text-xs font-semibold text-gray-600">
              学年
              <select
                v-model="form.serviceYears"
                class="mt-2 border border-gray-300 outline-none rounded-md py-2.5 px-3 bg-white text-gray-700 appearance-none w-full block focus:outline-none">
                <option
                  v-for="gradeOption in gradeOptions"
                  :key="gradeOption.value"
                  :value="gradeOption.value">
                  {{ gradeOption.label }}
                </option>
              </select>
            </label>
            <label class="text-xs font-semibold text-gray-600">
              学部・学科
              <input
                v-model="form.departmentKeyword"
                type="search"
                class="mt-2 form-input border border-gray-300 outline-none rounded-md py-2.5 px-4 bg-white placeholder-gray-400 text-gray-700 appearance-none w-full block focus:outline-none"
                placeholder="学部・学科を入力" />
            </label>
            <label class="text-xs font-semibold text-gray-600">
              名前
              <input
                v-model="form.nameKeyword"
                type="search"
                class="mt-2 form-input border border-gray-300 outline-none rounded-md py-2.5 px-4 bg-white placeholder-gray-400 text-gray-700 appearance-none w-full block focus:outline-none"
                placeholder="名前を入力" />
            </label>
          </div>
          <div class="mt-5 flex items-end justify-between gap-8">
            <div>
              <p class="text-xs font-semibold text-gray-600">
                低進捗フィルタ
              </p>
              <div class="mt-3 flex items-center gap-2 text-sm text-gray-700">
                <input
                  v-model="form.lowProgressThreshold"
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  maxlength="3"
                  class="w-20 border border-gray-300 outline-none rounded-md py-2 px-3 bg-white text-gray-700 text-center appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  @input="onLowProgressThresholdInput" />
                <span class="whitespace-nowrap">％未満を抽出</span>
              </div>
            </div>
            <div class="flex justify-end gap-4">
              <button
                class="min-w-[128px] bg-white hover:bg-gray-100 text-gray-700 py-2.5 px-6 border border-gray-300 rounded-md shadow-sm font-semibold text-xs whitespace-nowrap"
                @click.prevent="onResetButtonClicked">
                リセット
              </button>
              <button
                class="min-w-[108px] bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-7 rounded-md shadow-sm font-semibold text-xs whitespace-nowrap"
                @click.prevent="onSearchButtonClicked">
                検索
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="users.data.length"
        class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-3">
          <span class="text-sm font-semibold text-gray-700">並び替え</span>
          <select
            v-model="sortSelection"
            class="border border-gray-300 outline-none rounded-md py-2 px-3 bg-white text-sm text-gray-700 focus:outline-none"
            @change="onSortChanged">
            <option
              v-for="option in sortOptions"
              :key="option.value"
              :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
        <div class="flex items-center gap-5">
          <p class="text-sm text-gray-700">
            {{ paginationSummary }}
          </p>
          <pagination
            :paginate="users"
            @page-previous="onPaginationChanged"
            @page-next="onPaginationChanged"
            @page-click="onPaginationChanged" />
        </div>
      </div>

      <div class="shadow-lg bg-white rounded-lg overflow-hidden border border-gray-200">
        <div
          v-if="users.data.length"
          class="overflow-x-auto">
          <table class="min-w-full table-fixed">
            <thead class="bg-gray-100">
              <tr class="text-xs font-semibold text-gray-600">
                <th class="w-20 px-4 py-3 text-left">ユーザID</th>
                <th class="w-24 px-4 py-3 text-left">学籍番号</th>
                <th class="w-16 px-4 py-3 text-left">学年</th>
                <th class="w-32 px-4 py-3 text-left">学部・学科</th>
                <th class="w-36 px-4 py-3 text-left">名前</th>
                <th class="w-28 px-4 py-3 text-left">総合進捗</th>
                <th class="w-[520px] px-4 py-3 text-left">科目別進捗（5科目）</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 text-sm text-gray-700">
              <tr
                v-for="user in users.data"
                :key="user.id"
                class="hover:bg-gray-50">
                <td class="px-4 py-4 whitespace-nowrap">
                  {{ user.id ?? '-' }}
                </td>
                <td class="px-4 py-4 whitespace-nowrap">
                  {{ user.student_id ?? '-' }}
                </td>
                <td class="px-4 py-4 whitespace-nowrap">
                  {{ user.service_years ?? '-' }}
                </td>
                <td class="px-4 py-4">
                  <div class="truncate">
                    {{ user.department ?? '-' }}
                  </div>
                </td>
                <td class="px-4 py-4">
                  <div class="leading-5 break-words">
                    {{ user.name ?? '-' }}
                  </div>
                </td>
                <td class="px-4 py-4">
                  <div class="w-24">
                    <div class="text-base font-bold text-gray-900">
                      {{ user.total_progress.rate }}%
                    </div>
                    <div class="mt-0.5 text-xs text-gray-500">
                      {{ user.total_progress.answered }}/{{ user.total_progress.total }}問
                    </div>
                    <div class="mt-1.5">
                      <ProgressBar
                        :rate="user.total_progress.rate"
                        compact />
                    </div>
                  </div>
                </td>
                <td class="px-4 py-4">
                  <div class="grid grid-cols-5 gap-3">
                    <div
                      v-for="subject in user.subject_progress"
                      :key="`${user.id}-${subject.id}`"
                      class="min-w-0">
                      <div class="truncate text-[11px] font-semibold text-gray-600">
                        {{ subjectShortName(subject.name) }}
                      </div>
                      <div class="mt-1 text-xs font-semibold text-gray-900">
                        {{ subject.rate }}%
                      </div>
                      <div class="mt-1">
                        <ProgressBar
                          :rate="subject.rate"
                          compact />
                      </div>
                      <div class="mt-1 text-xs text-gray-500">
                        {{ subject.answered }}/{{ subject.total }}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div
            v-if="users.total > paginatePerPage"
            class="flex items-center justify-between px-4 py-4 border-t border-gray-200">
            <p class="text-sm text-gray-700">
              {{ paginationSummary }}
            </p>
            <div>
              <pagination
                :paginate="users"
                @page-previous="onPaginationChanged"
                @page-next="onPaginationChanged"
                @page-click="onPaginationChanged" />
            </div>
          </div>
        </div>
        <div
          v-else
          class="p-6">
          <div
            class="mx-auto w-11/12 bg-red-100 border border-red-400 text-red-500 px-4 py-3 rounded relative text-center"
            role="alert">
            <span class="block sm:inline font-bold">ユーザーが見つかりませんでした。</span>
          </div>
        </div>
      </div>
    </div>
  </AppClientSchoolLayout>
</template>

<script setup>
import { Inertia } from '@inertiajs/inertia'
import {
  reactive, ref, defineComponent, h, computed,
} from 'vue'
import AppClientSchoolLayout from '@/Layouts/AppClientSchoolLayout'
import Pagination from '../../../Common/Pagination'

const ProgressBar = defineComponent({
  props: {
    rate: {
      type: Number,
      default: 0,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const colorClass = () => {
      if (props.rate < 10) {
        return 'bg-red-500'
      }

      if (props.rate < 30) {
        return 'bg-yellow-400'
      }

      return 'bg-green-500'
    }

    return () => h('div', {
      class: [
        'w-full bg-gray-200 rounded-full overflow-hidden',
        props.compact ? 'h-2' : 'h-3',
      ],
    }, [
      h('div', {
        class: [
          `${colorClass()} rounded-full transition-all`,
          props.compact ? 'h-2' : 'h-3',
        ],
        style: { width: `${Math.min(Math.max(props.rate, 0), 100)}%` },
      }),
    ])
  },
})

const props = defineProps({
  users: {
    type: Object,
    default: () => ({}),
  },
  limit: {
    type: String,
    default: '15',
  },
  sort: {
    type: String,
    default: 'id',
  },
  order: {
    type: String,
    default: 'asc',
  },
  id: {
    type: String,
    default: null,
  },
  studentId: {
    type: String,
    default: null,
  },
  serviceYears: {
    type: String,
    default: null,
  },
  departmentKeyword: {
    type: String,
    default: null,
  },
  nameKeyword: {
    type: String,
    default: null,
  },
  lowProgressThreshold: {
    type: [String, Number],
    default: '',
  },
  schoolYears: {
    type: Number,
    default: 4,
  },
})

const paginatePerPage = ref('15')
const maxSchoolYears = Math.max(1, Math.min(props.schoolYears || 4, 4))
const gradeOptions = [
  { label: '全学年', value: 0 },
  ...Array.from({ length: maxSchoolYears }, (_, index) => ({
    label: `${index + 1}年生`,
    value: index + 1,
  })),
]

const sortOptions = [
  { label: '総合進捗率（降順）', value: 'total_progress_rate:desc' },
  { label: '総合進捗率（昇順）', value: 'total_progress_rate:asc' },
  { label: 'ユーザID（昇順）', value: 'id:asc' },
  { label: '学籍番号（昇順）', value: 'student_id:asc' },
  { label: '学年（昇順）', value: 'service_years:asc' },
  { label: '名前（昇順）', value: 'name:asc' },
]

const sortSelection = ref(`${props.sort}:${props.order}`)

const form = reactive(
  Inertia.form({
    id: props.id,
    studentId: props.studentId,
    serviceYears: props.serviceYears,
    departmentKeyword: props.departmentKeyword,
    nameKeyword: props.nameKeyword,
    lowProgressThreshold: props.lowProgressThreshold,
  }),
)

const paginationSummary = computed(() => {
  const from = props.users.from ?? 0
  const to = props.users.to ?? 0
  const total = props.users.total ?? 0

  return `${from}-${to}件 / 全${total}件`
})

const subjectShortName = (name) => name
  .replace('基礎動物看護学', '基礎動物看護')
  .replace('臨床動物看護学', '臨床動物看護')

const buildParams = (overrides = {}) => ({
  sort: props.sort,
  order: props.order,
  id: form.id,
  studentId: form.studentId,
  serviceYears: form.serviceYears,
  departmentKeyword: form.departmentKeyword,
  nameKeyword: form.nameKeyword,
  lowProgressThreshold: form.lowProgressThreshold,
  limit: props.limit,
  ...overrides,
})

const onLowProgressThresholdInput = (event) => {
  const value = event?.target?.value ?? ''
  form.lowProgressThreshold = String(value).replace(/\D/g, '').slice(0, 3)
}

const onSearchButtonClicked = () => {
  Inertia.get(route('school.appProgress', buildParams()))
}

const onResetButtonClicked = () => {
  form.id = ''
  form.studentId = ''
  form.serviceYears = ''
  form.departmentKeyword = ''
  form.nameKeyword = ''
  form.lowProgressThreshold = ''
  Inertia.get(route('school.appProgress', buildParams()))
}

const onSortChanged = () => {
  const [sort, order] = sortSelection.value.split(':')
  Inertia.get(route('school.appProgress', buildParams({
    sort,
    order,
  })))
}

const onPaginationChanged = (page) => {
  Inertia.get(route('school.appProgress', buildParams({
    page,
  })))
}
</script>
